import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

// Time your hit: stop the moving marker in the sweet-spot for maximum distance.
export default function GilliDanda() {
  const [marker, setMarker] = useState(0);
  const [phase, setPhase] = useState("ready"); // ready | swing | hit
  const [distance, setDistance] = useState(null);
  const [best, setBest] = useState(0);
  const dir = useRef(1);
  const raf = useRef(null);

  const start = () => {
    setPhase("swing"); setDistance(null); setMarker(0); dir.current = 1;
    const tick = () => {
      setMarker((m) => {
        let nm = m + dir.current * 2.2;
        if (nm >= 100) { nm = 100; dir.current = -1; }
        if (nm <= 0) { nm = 0; dir.current = 1; }
        return nm;
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };

  const hit = () => {
    if (phase !== "swing") return;
    cancelAnimationFrame(raf.current);
    // sweet spot 78-92
    const inSweet = marker >= 74 && marker <= 94;
    const near = Math.abs(marker - 84);
    let dist;
    if (marker < 20) dist = 0; // missed / duck
    else dist = Math.max(0, Math.round((100 - near) * 1.4 + (inSweet ? 30 : 0)));
    setDistance(dist); setPhase("hit"); setBest((b) => Math.max(b, dist));
    if (dist > 0) sfx.hit();
  };

  const reset = () => { setPhase("ready"); setMarker(0); setDistance(null); };
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  return (
    <div className="w-full text-center">
      <div className="flex justify-center gap-2">
        <AScore label="Distance" value={distance != null ? `${distance} m` : "—"} />
        <AScore label="Best" value={`${best} m`} />
      </div>
      <p className="my-3 text-sm text-kirana-brown/70">Tap HIT when the marker is in the green sweet-spot for a big shot!</p>

      {/* scene: danda swings, gilli flies on a good hit */}
      <div className="relative mx-auto mb-3 h-28 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border bg-gradient-to-b from-[#bfe3ff] to-[#eaf6ff]" data-testid="gd-scene">
        <div className="absolute inset-x-0 bottom-0 h-6 bg-[#c9a066]" />
        <span className="absolute bottom-6 left-3 text-[10px] font-bold text-kirana-brown/50">DANDA</span>
        {/* danda (striking stick) */}
        <div className={`absolute bottom-6 left-12 h-16 w-2 rounded-full bg-gradient-to-b from-[#a9784f] to-[#7a5230] ${phase === "hit" ? "gilli-swing" : ""}`} style={{ transformOrigin: "bottom center" }} />
        {/* gilli (small peg) */}
        <div className={`absolute bottom-9 left-16 h-2.5 w-7 rounded-full bg-[#8B5A2B] shadow ${phase === "hit" && distance > 0 ? "gilli-fly" : ""}`}
          style={{ "--gx": `${Math.min(320, (distance || 0) * 1.8)}px` }} data-testid="gd-gilli" />
        {phase === "hit" && distance >= 120 && <span className="absolute right-3 top-2 font-signboard text-sm font-bold text-kirana-red">Chhakka! 🚀</span>}
      </div>

      <div className="relative mx-auto h-8 w-full max-w-md overflow-hidden rounded-full border-[3px] border-kirana-brown bg-kirana-cream">
        <div className="absolute inset-y-0 bg-kirana-red/30" style={{ left: "0%", width: "20%" }} />
        <div className="absolute inset-y-0 bg-kirana-green/60" style={{ left: "74%", width: "20%" }} />
        <div data-testid="gd-marker" className="absolute top-1/2 h-9 w-1.5 -translate-y-1/2 rounded bg-kirana-brown" style={{ left: `${marker}%` }} />
      </div>

      <div className="mt-5">
        {phase === "ready" && <ABtn data-testid="gd-start" onClick={start} tone="gold" className="px-8 py-3">Start Swing</ABtn>}
        {phase === "swing" && <ABtn data-testid="gd-hit" onClick={hit} tone="red" className="px-10 py-3 text-lg">HIT! 🏏</ABtn>}
        {phase === "hit" && (
          <>
            <AResult testId="gd-result" win={distance >= 60} text={distance === 0 ? "Missed! 😅" : distance >= 120 ? `Chhakka! ${distance}m 🚀` : `${distance} metres!`} />
            <div className="mt-3 flex justify-center"><ABtn data-testid="gd-again" onClick={reset} tone="green">Hit Again</ABtn></div>
          </>
        )}
      </div>
    </div>
  );
}
